// src/pages/PuterPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { FiUpload, FiSend, FiVolume2, FiVolumeX, FiPause, FiPlay, FiSearch, FiCreditCard, FiShoppingCart, FiX, FiPhone, FiCheckCircle, FiCopy, FiCheck, FiCamera } from "react-icons/fi";

const PuterPage = () => {
  const [imageSrc, setImageSrc] = useState(null);
  const [imageDescription, setImageDescription] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [loadingImage, setLoadingImage] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const [message, setMessage] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [selectedFile, setSelectedFile] = useState(null);
  const [userCredits, setUserCredits] = useState(0);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authModalVisible, setAuthModalVisible] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [verificationStep, setVerificationStep] = useState(1);
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [showOtpCopy, setShowOtpCopy] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [facingMode, setFacingMode] = useState("environment"); // 'environment' for back camera, 'user' for front

  const utteranceRef = useRef(null);
  const speechSynthesisRef = useRef(null);
  const charIndexRef = useRef(0);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Load Puter script
  useEffect(() => {
    if (!window.puter) {
      const script = document.createElement("script");
      script.src = "https://js.puter.com/v2/";
      script.async = true;
      document.body.appendChild(script);
    }

    // Initialize speech synthesis
    speechSynthesisRef.current = window.speechSynthesis;

    // Create a single utterance instance
    utteranceRef.current = new SpeechSynthesisUtterance();

    // Set up event listeners
    utteranceRef.current.onend = () => {
      setIsPlaying(false);
      charIndexRef.current = 0;
    };

    utteranceRef.current.onerror = () => {
      setIsPlaying(false);
    };

    // Check if user is authenticated
    const checkAuthStatus = () => {
      const storedAuth = localStorage.getItem('userAuthenticated');
      const storedCredits = localStorage.getItem('userCredits');
      const storedPhone = localStorage.getItem('userPhone');

      if (storedAuth === 'true') {
        setIsAuthenticated(true);
        setUserCredits(storedCredits ? parseInt(storedCredits) : 20);
        setPhoneNumber(storedPhone || '');
      } else {
        setAuthModalVisible(true);
      }
    };

    checkAuthStatus();

    // Clean up on component unmount
    return () => {
      if (speechSynthesisRef.current) {
        speechSynthesisRef.current.cancel();
      }
      stopCamera();
    };
  }, []);

  // Countdown timer for OTP resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Update volume when it changes
  useEffect(() => {
    if (utteranceRef.current) {
      utteranceRef.current.volume = volume;
    }
  }, [volume]);

  // Open camera with specified facing mode
  const openCamera = async (mode) => {
    setFacingMode(mode);
    setShowCameraModal(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode }
      });
      setCameraStream(stream);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      setMessage({ type: "error", text: "Could not access camera: " + err.message });
      setShowCameraModal(false);
    }
  };

  // Stop camera stream
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
  };

  // Capture photo from camera
  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw the current video frame to the canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob and create a file
      canvas.toBlob((blob) => {
        const file = new File([blob], "camera-capture.jpg", { type: "image/jpeg" });
        setSelectedFile(file);

        // Create a preview URL
        const url = URL.createObjectURL(blob);
        setImageSrc(url);

        // Close camera and clean up
        setShowCameraModal(false);
        stopCamera();
        setImageDescription("");
      }, "image/jpeg");
    }
  };

  // Switch between front and back camera
  const switchCamera = () => {
    const newMode = facingMode === "user" ? "environment" : "user";
    stopCamera();
    openCamera(newMode);
  };
  const handleClearImage = () => {
    setSelectedFile(null);
    setImageSrc(null);
    setImageDescription(null);
  };

  // Simulate sending OTP (for development/demo)
  const sendOtp = () => {
    if (!phoneNumber.trim()) {
      setMessage({ type: "error", text: "Please enter a valid phone number" });
      return;
    }

    // Generate a random 6-digit OTP
    const newOtp = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedOtp(newOtp);

    // Store the OTP and timestamp
    localStorage.setItem('otp', newOtp);
    localStorage.setItem('otpTimestamp', Date.now().toString());

    setOtpSent(true);
    setVerificationStep(2);
    setCountdown(60);
    setShowOtpCopy(true);
    setMessage({ type: "success", text: "OTP generated for demo purposes. Check the copy button below." });
  };

  // Verify the entered OTP
  const verifyOtp = () => {
    const storedOtp = localStorage.getItem('otp');
    const otpTimestamp = localStorage.getItem('otpTimestamp');

    // Check if OTP exists and is not expired (10 minutes)
    if (!storedOtp || !otpTimestamp || (Date.now() - parseInt(otpTimestamp)) > 600000) {
      setMessage({ type: "error", text: "OTP has expired. Please request a new one." });
      return;
    }

    if (otp === storedOtp) {
      // Successful verification
      setIsAuthenticated(true);
      setAuthModalVisible(false);
      setUserCredits(20);
      localStorage.setItem('userAuthenticated', 'true');
      localStorage.setItem('userCredits', '20');
      localStorage.setItem('userPhone', phoneNumber);
      localStorage.removeItem('otp');
      localStorage.removeItem('otpTimestamp');
      setMessage({ type: "success", text: "Phone number verified! 20 free credits added to your account." });
    } else {
      setMessage({ type: "error", text: "Invalid OTP. Please try again." });
    }
  };

  // Copy OTP to clipboard
  const copyOtpToClipboard = () => {
    navigator.clipboard.writeText(generatedOtp)
      .then(() => {
        setMessage({ type: "success", text: "OTP copied to clipboard!" });
      })
      .catch(err => {
        setMessage({ type: "error", text: "Failed to copy OTP. Please manually type: " + generatedOtp });
      });
  };

  const deductCredits = (amount) => {
    const newCredits = userCredits - amount;
    setUserCredits(newCredits);
    localStorage.setItem('userCredits', newCredits.toString());
    return newCredits;
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setMessage(null);

    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64 = event.target.result;
      setImageSrc(base64);
      setImageDescription("");
    };
    reader.readAsDataURL(file);
  };

  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (imageDescription) {
      navigator.clipboard.writeText(imageDescription);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500); // revert after 1.5s
    }
  };

  const [copiedIndex, setCopiedIndex] = useState(null);

  const handleCopyChat = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 1500); // revert after 1.5s
  };

  const handleAnalyze = async () => {
    if (!selectedFile) {
      setMessage({ type: "error", text: "Please select an image first." });
      return;
    }

    if (userCredits < 5) {
      setShowSubscriptionModal(true);
      return;
    }

    setLoadingImage(true);
    setMessage(null);

    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64 = event.target.result;

      try {
        const res = await window.puter.ai.chat(
          "Describe this photo in detail",
          base64,
          { model: "gpt-5-nano" }
        );
        const text =
          res?.message?.content ||
          res?.message?.content?.[0]?.text ||
          JSON.stringify(res, null, 2);
        setImageDescription(text);

        const newCredits = deductCredits(5);
        setMessage({
          type: "success",
          text: `Image analyzed successfully! 5 credits deducted. Remaining: ${newCredits}`
        });
      } catch (err) {
        setMessage({ type: "error", text: "Failed to analyze image." });
      } finally {
        setLoadingImage(false);
      }
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleSpeak = () => {
    if (!imageDescription) return;

    if (isPlaying) {
      speechSynthesisRef.current.pause();
      const utterance = utteranceRef.current;
      const elapsedTime = (performance.now() - utterance.startTime) / 1000;
      const wordsSpoken = Math.floor(elapsedTime * 2.5);
      charIndexRef.current += wordsSpoken * 5;
      setIsPlaying(false);
    } else {
      if (speechSynthesisRef.current.paused && charIndexRef.current > 0) {
        const textToSpeak = imageDescription.substring(charIndexRef.current);
        utteranceRef.current.text = textToSpeak;
        speechSynthesisRef.current.resume();
      } else {
        speechSynthesisRef.current.cancel();
        charIndexRef.current = 0;

        utteranceRef.current.text = imageDescription;

        const voices = speechSynthesisRef.current.getVoices();
        const femaleVoice = voices.find(
          (v) => v.name.toLowerCase().includes("female") || v.gender === "female"
        );
        if (femaleVoice) utteranceRef.current.voice = femaleVoice;
        else if (voices.length > 0) utteranceRef.current.voice = voices[0];

        utteranceRef.current.rate = 1;
        utteranceRef.current.pitch = 1.2;
        utteranceRef.current.volume = volume;

        utteranceRef.current.startTime = performance.now();
        speechSynthesisRef.current.speak(utteranceRef.current);
      }
      setIsPlaying(true);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
  };

  const handleSend = async () => {
    const messageText = chatInput.trim();
    if (!messageText) return;

    if (userCredits < 2) {
      setShowSubscriptionModal(true);
      return;
    }

    setChatMessages((prev) => [...prev, { sender: "You", text: messageText }]);
    setChatInput("");
    setLoadingChat(true);

    try {
      const res = await window.puter.ai.chat(messageText, { model: "gpt-5-nano" });
      const text =
        res?.message?.content ||
        res?.message?.content?.[0]?.text ||
        JSON.stringify(res, null, 2);
      setChatMessages((prev) => [...prev, { sender: "AI", text }]);

      const newCredits = deductCredits(2);
      setMessage({
        type: "success",
        text: `Message sent! 2 credits deducted. Remaining: ${newCredits}`
      });
    } catch (err) {
      setMessage({ type: "error", text: "Failed to get AI response." });
    } finally {
      setLoadingChat(false);
    }
  };

  const handlePurchase = (plan) => {
    let creditsToAdd = 0;
    let planName = "";

    switch (plan) {
      case "basic":
        creditsToAdd = 20;
        planName = "Basic Plan";
        break;
      case "pro":
        creditsToAdd = 50;
        planName = "Pro Plan";
        break;
      case "premium":
        creditsToAdd = 100;
        planName = "Premium Plan";
        break;
      default:
        creditsToAdd = 20;
    }

    const newCredits = userCredits + creditsToAdd;
    setUserCredits(newCredits);
    localStorage.setItem('userCredits', newCredits.toString());
    setShowSubscriptionModal(false);
    setMessage({ type: "success", text: `Thanks for purchasing the ${planName}! ${creditsToAdd} credits added to your account.` });
  };

  // Subscription Plans Component
  const SubscriptionPlans = ({ compact = false, onSelectPlan }) => {
    const plans = [
      { id: "basic", name: "Basic Plan", price: "$4.99", credits: 20, description: "Perfect for casual users" },
      { id: "pro", name: "Pro Plan", price: "$9.99", credits: 50, description: "Great for regular users" },
      { id: "premium", name: "Premium Plan", price: "$19.99", credits: 100, description: "Best for power users" }
    ];

    return (
      <div className={`grid ${compact ? "grid-cols-1 gap-4" : "grid-cols-1 md:grid-cols-3 gap-6"} mt-4`}>
        {plans.map((plan) => (
          <div key={plan.id} className="bg-gray-700 p-4 rounded-lg border border-gray-600">
            <h3 className="text-lg font-semibold text-white mb-2">{plan.name}</h3>
            <div className="text-2xl font-bold text-purple-300 mb-2">{plan.price}</div>
            <div className="text-gray-300 mb-3">{plan.credits} credits</div>
            <p className="text-gray-400 text-sm mb-4">{plan.description}</p>
            <button
              onClick={() => onSelectPlan(plan.id)}
              disabled={true}
              className="w-full bg-gray-500 text-gray-300 py-2 rounded-lg cursor-not-allowed transition-colors"
            >
              Purchase
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="pb-20 pt-10 min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900 p-6 text-white">
      {/* Camera Modal */}
      {showCameraModal && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-4 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-purple-300 mb-4 text-center">Take a Photo</h2>

            <div className="relative mb-4">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full h-64 bg-black rounded-lg"
              />
              <canvas ref={canvasRef} className="hidden" />
            </div>

            <div className="flex justify-center gap-4 mb-4">
              <button
                onClick={switchCamera}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Switch Camera
              </button>

              <button
                onClick={capturePhoto}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 transition-colors flex items-center gap-2"
              >
                <FiCamera className="text-lg" /> Capture
              </button>
            </div>

            <button
              onClick={() => {
                setShowCameraModal(false);
                stopCamera();
              }}
              className="w-full bg-gray-600 text-white py-2 rounded-lg hover:bg-gray-500 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Authentication Modal */}
      {authModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold text-purple-300 mb-4">Phone Verification Required</h2>
            <p className="text-gray-300 mb-6">
              {verificationStep === 1
                ? "Verify your phone number to get 20 free credits"
                : `Enter the 6-digit code sent to ${phoneNumber}`}
            </p>

            {verificationStep === 1 ? (
              <div>
                <div className="mb-4">
                  <label className="block text-gray-300 mb-2">Phone Number</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiPhone className="text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="Enter your phone number"
                      className="w-full pl-10 px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    For demo purposes, any phone number will work
                  </p>
                </div>
                <button
                  onClick={sendOtp}
                  className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-500 transition-colors"
                >
                  Send Verification Code
                </button>
              </div>
            ) : (
              <div>
                <div className="mb-4">
                  <label className="block text-gray-300 mb-2">Verification Code</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  {showOtpCopy && (
                    <div className="mt-3 p-3 bg-gray-900 rounded-lg">
                      <p className="text-sm text-gray-300 mb-2">
                        For demo purposes, use this OTP: <span className="font-mono text-purple-300">{generatedOtp}</span>
                      </p>
                      <button
                        onClick={copyOtpToClipboard}
                        className="flex items-center gap-2 text-sm bg-gray-700 text-white px-3 py-1 rounded hover:bg-gray-600 transition-colors"
                      >
                        <FiCopy className="text-sm" /> Copy OTP to Clipboard
                      </button>
                    </div>
                  )}
                </div>
                <button
                  onClick={verifyOtp}
                  className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-500 transition-colors mb-3"
                >
                  Verify & Get 20 Free Credits
                </button>

                <div className="text-center">
                  {countdown > 0 ? (
                    <p className="text-gray-400 text-sm">
                      Resend code in {countdown} seconds
                    </p>
                  ) : (
                    <button
                      onClick={sendOtp}
                      className="text-purple-400 hover:text-purple-300 text-sm"
                    >
                      Resend code
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="mt-6 p-3 bg-gray-900 rounded-lg">
              <h3 className="text-sm font-semibold text-purple-300 mb-2">Why am I not receiving OTPs?</h3>
              <p className="text-xs text-gray-400">
                This is a demo application. In a production environment, you would integrate with an SMS service like Twilio, MessageBird, or similar providers to send real OTPs to users' phones.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div
            className="
      bg-gray-800 rounded-xl 
      w-full mx-4 
      max-w-md md:max-w-3xl 
      h-[80vh] md:h-auto
      overflow-y-auto 
      p-6
      mt-20
      text-center sm:text-left
      border border-gray-700
    "
          >
            <h2 className="text-2xl font-bold text-purple-300 mb-4">
              Choose a Subscription Plan
            </h2>
            <p className="text-gray-300 mb-6">
              Select a plan to continue using our AI services.
            </p>

            <SubscriptionPlans onSelectPlan={handlePurchase} />

            <button
              onClick={() => setShowSubscriptionModal(false)}
              className="w-full bg-gray-600 text-white py-2 rounded-lg hover:bg-gray-500 transition-colors mt-6"
            >
              Maybe Later
            </button>
          </div>
        </div>

      )}

      {/* Header with Credit Display and Subscription Button */}
      <header className="mb-8 text-center mt-20">
        <h1 className="text-4xl font-bold text-white mb-2">AI Vision Assistant</h1>
        {isAuthenticated && (
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="inline-flex items-center bg-purple-700 px-4 py-2 rounded-full">
              <FiCreditCard className="mr-2" />
              <span>{userCredits} Credits Available</span>
            </div>
            <div className="inline-flex items-center text-sm text-gray-300">
              <FiCheckCircle className="mr-1 text-green-400" />
              <p>
                Verified{phoneNumber ? `: ${phoneNumber}` : ""}
              </p>

            </div>
            <button
              onClick={() => setShowSubscriptionModal(true)}
              className="inline-flex items-center bg-indigo-600 hover:bg-indigo-500 px-4 py-2 rounded-lg transition-colors"
            >
              <FiShoppingCart className="mr-2" />
              Purchase More Credits
            </button>
          </div>
        )}
      </header>

      {isAuthenticated ? (
        <div className="max-w-6xl mx-auto">
          {/* Subscription Plans Section - Always Visible */}
          <div className="bg-gray-800 rounded-xl shadow-2xl p-6 border border-gray-700 mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 
                  items-center text-center sm:text-left">
              <h2 className="text-2xl font-semibold text-purple-300">
                Subscription Plans
              </h2>
              <button
                onClick={() => setShowSubscriptionModal(true)}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 transition-colors w-full sm:w-auto"
              >
                <FiShoppingCart className="text-lg" />
                View All Plans
              </button>
            </div>
          </div>


          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Side - Image Analysis */}
            <div
              className="
      bg-gray-800 rounded-xl shadow-2xl p-6 flex flex-col border border-gray-700
      h-[80vh] sm:h-[70vh] md:h-[60vh]
    "
            >
              <h2 className="text-2xl font-semibold text-purple-300 mb-4 text-center">
                Image Analysis (5 credits)
              </h2>

              {/* Scrollable area */}
              <div className="flex-1 overflow-y-auto">
                <div className="mb-6">
                  <h3 className="text-lg font-medium mb-3 text-white">1. Upload Photo</h3>

                  <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <label className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-700 text-white rounded-lg hover:bg-purple-600 cursor-pointer transition-colors">
                      <FiUpload className="text-lg" />
                      Choose File
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="hidden"
                      />
                    </label>

                    <button
                      onClick={() => openCamera("environment")}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors"
                    >
                      <FiCamera className="text-lg" />
                      Camera
                    </button>

                    <button
                      onClick={handleAnalyze}
                      disabled={!selectedFile || loadingImage}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <FiSearch className="text-lg" />
                      Analyze
                    </button>
                  </div>

                  {/* Guide steps when nothing uploaded */}
                  {!selectedFile && !imageSrc && !imageDescription && (
                    <ul className="list-decimal list-inside text-gray-300 space-y-4 text-sm bg-gray-700/50 p-4 rounded-lg mt-10">
                      <li>Select an image file by clicking "Choose File" or use the camera to take a photo.</li>
                      <li>After selecting, click "Analyze" to process the image.</li>
                      <li>Wait a moment for AI to generate the description.</li>
                      <li>Once processed, you can play audio or copy the description.</li>
                    </ul>
                  )}

                  {/* Small status when file chosen */}
                  {selectedFile && (
                    <div className="text-sm text-gray-300 italic mt-2">
                      File selected. Click Analyze to process.
                    </div>
                  )}


                </div>

                {loadingImage && (
                  <div className="animate-pulse text-center mb-4 bg-gradient-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent font-semibold text-lg">
                    ⏳ Analyzing image... This may take 1–2 minutes
                  </div>

                )}

                {imageSrc && (

                  <div className="mb-4 flex flex-col items-center gap-3">
                    <button
                      onClick={handleClearImage}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg 
                 hover:bg-red-500 transition-colors shadow-md"
                    >
                      <FiX className="text-lg" />
                      Clear Image
                    </button>
                    <img
                      src={imageSrc}
                      alt="Preview"
                      className="object-contain"
                      style={{ width: "100%", height: "250px" }}
                    />

                    {/* Clear Image Button */}

                  </div>
                )}

                {imageDescription && (
                  <div className="relative p-4 bg-gray-700 rounded-lg">
                    <div className="flex justify-between items-start">
                      <p className="whitespace-pre-line text-white mb-4">
                        {imageDescription}
                      </p>

                      <button
                        onClick={handleCopy}
                        className="ml-2 p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 transition-colors flex items-center gap-1"
                      >
                        {copied ? (
                          <>
                            <FiCheck className="text-lg" /> Copied
                          </>
                        ) : (
                          <>
                            <FiCopy className="text-lg" /> Copy
                          </>
                        )}
                      </button>
                    </div>

                    <div className="flex items-center justify-between mt-4">
                      <button
                        onClick={handleSpeak}
                        className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 transition-colors"
                      >
                        {isPlaying ? (
                          <>
                            <FiPause className="text-lg" /> Pause
                          </>
                        ) : (
                          <>
                            <FiPlay className="text-lg" /> Play
                          </>
                        )}
                      </button>

                      <div className="flex items-center gap-2">
                        {volume > 0 ? (
                          <FiVolume2 className="text-purple-300" />
                        ) : (
                          <FiVolumeX className="text-purple-300" />
                        )}
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={volume}
                          onChange={handleVolumeChange}
                          className="w-24 accent-purple-500"
                        />
                        <span className="text-xs text-gray-300 w-8">
                          {Math.round(volume * 100)}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Right Side - Chat */}
<div
  className="
    bg-gray-800 rounded-xl shadow-2xl p-6 flex flex-col border border-gray-700
    h-[100vh] sm:h-[70vh] md:h-[60vh]
  "
>
  <h2 className="text-2xl font-semibold text-purple-300 mb-4 text-center">
    2. AI Chat Assistant (2 credits/message)
  </h2>

  {/* Messages area */}
  <div className="flex-1 overflow-y-auto p-4 bg-gray-900 rounded-lg mb-4 flex flex-col gap-4">
    {chatMessages.length === 0 ? (
      <div className="flex flex-col items-center justify-center text-gray-300 text-center my-auto space-y-6">
        <div>
          <h3 className="text-xl font-semibold text-purple-300 mb-2">
            👋 Welcome to AI Chat Assistant
          </h3>
          <p className="text-sm text-gray-400">
            This assistant will help you with queries and analysis.
          </p>
        </div>

        <ul className="list-disc text-left space-y-3 text-sm bg-gray-800/70 p-4 rounded-lg w-full px-8">
          <li>Type your question in the input box below.</li>

          <li>
            Press{" "}
            <kbd className="px-2 py-1 bg-gray-700 rounded">Enter</kbd> or click{" "}
            <span className="text-purple-400">Send</span> to chat.
          </li>
          <li>You can copy AI’s replies for later use.</li>
        </ul>

        <p className="text-xs text-gray-500 italic">
          Start by asking me anything — e.g., “What is the best plant disease detector?”
        </p>
      </div>
    ) : (
      chatMessages.map((m, i) => (
        <div
          key={i}
          className={`relative p-3 rounded-lg max-w-[80%] ${
            m.sender === "You"
              ? "bg-purple-700 self-end"
              : "bg-gray-700 self-start"
          }`}
        >
          <div className="flex justify-between items-start gap-2">
            <div className="flex-1">
              <strong
                className={
                  m.sender === "You" ? "text-purple-100" : "text-white"
                }
              >
                {m.sender}:
              </strong>
              <div className="mt-1 whitespace-pre-line text-white">
                {m.text}
              </div>
            </div>

            <button
              onClick={() => handleCopyChat(m.text, i)}
              className="ml-2 p-1 bg-purple-600 text-white rounded hover:bg-purple-500 transition-colors flex items-center gap-1 text-xs"
            >
              {copiedIndex === i ? (
                <>
                  <FiCheck className="text-sm" /> Copied
                </>
              ) : (
                <>
                  <FiCopy className="text-sm" /> Copy
                </>
              )}
            </button>
          </div>
        </div>
      ))
    )}

    {loadingChat && (
      <div className="text-purple-300 animate-pulse">AI is thinking...</div>
    )}
  </div>

  {/* Input area pinned at bottom */}
  <div className="flex flex-col sm:flex-row gap-2">
    <input
      type="text"
      placeholder="Type your message..."
      value={chatInput}
      onChange={(e) => setChatInput(e.target.value)}
      onKeyPress={(e) => e.key === "Enter" && handleSend()}
      className="flex-1 px-4 py-3 bg-gray-700 text-white border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
    />
    <button
      onClick={handleSend}
      disabled={!chatInput.trim()}
      className="px-4 py-3 bg-purple-700 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
    >
      <FiSend /> Send
    </button>
  </div>
</div>

          </div>

        </div>
      ) : (
        <div className="text-center py-10">
          <p className="text-xl text-gray-300">Please verify your phone number to use the AI Vision Assistant</p>
        </div>
      )}

      {/* Status Message */}
      {message && (
        <div className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-lg ${message.type === "error" ? "bg-red-600" : "bg-green-600"}`}>
          {message.text}
        </div>
      )}
    </div>
  );
};

export default PuterPage;